"use client";
import { Pause, Play, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function WebStorySlider({ stories = [], duration = 5000 }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const progressRef = useRef(null);

  const activeStory = stories[current];

  useEffect(() => {
    if (paused) return;

    progressRef.current.style.animation = "none";
    progressRef.current.offsetHeight; // reset animation
    progressRef.current.style.animation = `progress ${duration}ms linear forwards`;

    const timer = setTimeout(() => {
      handleNext();
    }, duration);

    return () => clearTimeout(timer);
  }, [current, paused, duration]);

  const handleNext = () => {
    setCurrent((prev) => (prev < stories.length - 1 ? prev + 1 : prev));
  };

  const handlePrev = () => {
    setCurrent((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const togglePause = () => {
    setPaused((p) => !p);
    if (progressRef.current) {
      progressRef.current.style.animationPlayState = paused
        ? "running"
        : "paused";
    }
  };

  const handleStoryClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const halfWidth = rect.width / 2;

    if (clickX < halfWidth) {
      handlePrev();
    } else {
      handleNext();
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Check this out",
          text: "Dekho ye",
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied!");
      }
    } catch (error) {
      console.log("Share cancelled", error);
    }
  };

  if (!stories || stories.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white">
        <p>No stories available</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex justify-center items-center z-50 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src={activeStory.image || "/placeholder.svg"}
          alt="bg"
          className="w-full h-full object-cover scale-110 blur-2xl transition-transform duration-[1800ms] ease-out animate-ken-burns"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>

      <div className="relative flex items-center justify-center w-full h-full max-w-xl">
        {/* Left Navigation Button */}
        {current > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-20"
          >
            <svg
              className="w-6 h-6 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Story Container */}
        <div
          className="relative w-full md:max-w-sm h-full md:h-[80vh] md:rounded-xl overflow-hidden cursor-pointer group"
          onClick={handleStoryClick}
        >
          {/* Image with Ken Burns effect */}
          <img
            src={activeStory.image || "/placeholder.svg"}
            alt="story"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out scale-[1.03] group-hover:scale-110 animate-ken-burns"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />

          {/* Progress Bars */}
          <div className="absolute top-2 left-2 right-2 flex gap-1">
            {stories.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden"
              >
                {i === current && (
                  <div
                    ref={progressRef}
                    className="h-full bg-gradient-to-r from-white via-white to-white/80 rounded-full w-0 shadow-[0_0_8px_rgba(255,255,255,0.6)]"
                  />
                )}
                {i < current && (
                  <div className="h-full bg-white rounded-full w-full" />
                )}
                {i > current && (
                  <div className="h-full bg-white/20 rounded-full w-full" />
                )}
              </div>
            ))}
          </div>

          {/* Pause / Play */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              togglePause();
            }}
            className="absolute top-6 right-16 text-white text-2xl hover:scale-110 transition-transform"
          >
            {paused ? <Play /> : <Pause />}
          </button>

          <button
            onClick={handleShare}
            className="absolute top-6 right-6 text-white text-2xl hover:scale-110 transition-transform"
          >
            <Send size={22} />
          </button>

          {/* Content with slide-up animation */}
          <div className="absolute bottom-20 px-10 text-white">
            <h2
              key={`title-${current}`}
              className="text-[26px] font-semibold mb-2 animate-slide-up-fade"
              style={{ lineHeight: "31px" }}
            >
              {activeStory.title}
            </h2>
            <p
              key={`desc-${current}`}
              className="text-[17px] text-gray-200 animate-slide-up-fade animation-delay-150"
              style={{ lineHeight: "20px", fontWeight: 400 }}
            >
              {activeStory.description}
            </p>
          </div>

          {/* CTA with gentle pulse */}
          {/* <div className="absolute bottom-6 w-full flex justify-center">
            <button
              onClick={(e) => e.stopPropagation()}
              className="
            
                flex items-center gap-2
                bg-white text-black
                px-2 py-1
                rounded-full
                text-sm font-semibold
                shadow-md
                transition-all duration-300 ease-out
                hover:-translate-y-1
                hover:shadow-xl
                active:scale-95
    "
            >
              <span
                className=" 
        flex items-center justify-center
        w-7 h-7
        rounded-full
        bg-[#E8E8E8] text-white
        transition-transform duration-300
        group-hover:-translate-y-0.5
      "
              >
                <Link2 color="#000" size={16} className="rotate-45" />
              </span>
              पूरी स्टोरी पढ़ें
            </button>
          </div> */}
        </div>

        {/* Right Navigation Button */}
        {current < stories.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-20"
          >
            <svg
              className="w-6 h-6 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to   { width: 100%; }
        }

        @keyframes ken-burns {
          0%   { transform: scale(1.03) translate(0, 0); }
          100% { transform: scale(1.12) translate(-2%, -2%); }
        }

        @keyframes slide-up-fade {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }

        .animate-ken-burns {
          animation: ken-burns 16000ms ease-in-out infinite alternate;
        }

        .animate-slide-up-fade {
          animation: slide-up-fade 0.75s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .animation-delay-150 {
          animation-delay: 0.15s;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
